import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ConfirmModal from "components/ui/ConfirmModal";
import { useAuth } from "context/AuthContext";
import { canRequestLessonAs } from "constants/permissions";
import {
  captureLandingParams,
  getLandingType,
  hasPendingLandingCta,
  consumeLandingCta,
} from "util/landingParams";
import { trackClick, trackLanding } from "util/appAnalytics";

/**
 * 인스타 광고 유입(?lp=teacher|academy|student) 1회 안내 CTA.
 *  - 진입 URL 의 lp/utm 을 캡처(렌더 중 동기 호출 → Start 의 /home replace 전에 확보)하고,
 *    유효한 lp 면 기존 ConfirmModal(2버튼)로 "웹에서 실제로 완주 가능한 다음 단계"를 유도.
 *  - 확인 버튼 = 작성 흐름(로그인 필요 라우트는 RequireAuth 가 자동으로 ?redirect= 보존),
 *    취소 버튼 = 관련 목록 열람. 둘 다 견적/채팅 없이 웹에서 진행 가능한 경로만.
 *  - 새 CSS 없음: ConfirmModal(modal-dimmed/.modal/tit-box/con-box/btn-group)만 재사용.
 */

// lp 유형별 안내 문구 + 두 버튼의 목적지 (웹 완주 가능 경로만)
const CTA = {
  // ★ 2026-11-02 광고 문구("강사 채용, 대타, 오디션 등 무용 일자리를 한곳에서")와 맞춘다.
  //  종전엔 '이력서 등록하기'가 주 버튼이라 광고 약속과 어긋났다 — 광고 유입 318명 중 이력서 4명(1%), 팝업에서 이탈 36%.
  //  공고 보기를 주 버튼으로, 이력서 등록을 보조 버튼으로 바꾼다.
  teacher: {
    title: "무용 일자리, 한곳에서 찾아보세요",
    body: "강사 채용·대타·오디션 공고를 모아 보여드려요. 이력서를 등록해 두면 학원에서 먼저 채용 제안을 받을 수 있어요.",
    confirmText: "채용공고 보기",
    confirmTo: "/jobs?tab=hire", // 강사 = 일자리를 찾는다 → 구인 탭
    cancelText: "이력서 등록",
    cancelTo: "/resumeEdit", // guest 라우트 — 비회원도 작성해보고 저장 시 가입 유도
  },
  academy: {
    title: "원장님, 무용연에 오신 걸 환영해요",
    body: "채용공고를 등록하면 조건에 맞는 강사들이 지원해요. 먼저 다른 학원 공고를 살펴봐도 좋아요.",
    confirmText: "채용공고 등록하기",
    confirmTo: "/jobs/create", // guest 라우트 — 작성 후 가입·인증 안내
    // 둘러보기는 채용 화면으로 한정하지 않고 비회원도 볼 수 있는 일반 홈으로 보낸다.
    cancelText: "공고 둘러보기",
    cancelTo: "/home"
  },
  student: {
    title: "무용연에 오신 걸 환영해요",
    body: "관심 있는 무용 레슨과 강사를 자유롭게 둘러보세요.",
    confirmText: "레슨 요청하기",
    confirmTo: "/quoteRequest", // guest 라우트 — 문진 완주 후 가입 유도
    cancelText: "레슨 둘러보기",
    cancelTo: "/lessons",
  },
};

// CTA 를 덮으면 안 되는(로그인/가입/약관 등) 경로
const SUPPRESS = ["/login", "/register", "/signupTerms", "/social-signup", "/oauth/kakao", "/naverAuth", "/maintenance"];
// 광고 링크가 곧바로 작성 화면을 연 경우(?lp=teacher_sub → /subs/create, ?lp=academy_job → /jobs/create).
//  이미 광고가 약속한 화면에 와 있으므로 안내 팝업을 띄우지 않고 1회 노출분만 소비한다.
//  /hire = 원장 광고 선택 화면(채용 / 대타). /dance-diet = 회원 무용 다이어트 광고 첫 화면(11-05, 버튼이 모두 레슨 요청).
const LANDED_ON_TASK = ["/subs/create", "/jobs/create", "/hire", "/quoteRequest", "/dance-diet"]; // 할 일 화면으로 바로 온 광고는 안내 팝업 없이(10-24 회원 광고)

export default function LandingCtaHost() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeType } = useAuth();

  // 렌더 시점에 즉시 캡처(효과보다 먼저) → Start 가 /home 으로 replace 하며 쿼리를 버리기 전에 lp/utm 확보
  useState(() => { captureLandingParams(); trackLanding(); return null; });

  const [show, setShow] = useState(false);
  const [type, setType] = useState(null);

  useEffect(() => {
    if (!hasPendingLandingCta()) return;
    if (SUPPRESS.includes(location.pathname)) return; // 인증 흐름 위에는 띄우지 않음(다음 진입에서 노출)
    if (LANDED_ON_TASK.includes(location.pathname)) { consumeLandingCta(); return; }
    const lp = getLandingType();
    // ★ 2026-11-02 강사 일자리 광고(lp=teacher)는 팝업 없이 바로 채용공고 목록으로.
    //  팝업은 닫기가 없어 둘 중 하나를 골라야 했고, 광고 유입 318명 중 36%가 아무것도 안 누르고 나갔으며
    //  누른 사람의 98%가 '채용공고 보기'였다. 목록 맨 위에는 가입 안내(JobBoardSignupCta)가 있다.
    //  ⚠️ '/' 에서는 기다린다 — Start 가 곧 /home 으로 바꾸는데, 여기서 먼저 /jobs 로 가면 Start 가 /home 으로 덮어쓴다.
    //  ⚠️ 주소 뒤 파라미터(utm·fbclid)를 함께 넘긴다 — 늦게 받아지는 Meta 픽셀이 fbclid 를 읽어야 한다(Start.js 주석).
    if (lp === "teacher") {
      if (location.pathname === "/") return;
      consumeLandingCta();
      if (location.pathname === "/home") {
        const sp = new URLSearchParams(location.search);
        sp.set("tab", "hire");
        trackClick("cta:landing_teacher", { props: { choice: "auto", to: "/jobs?tab=hire" } });
        navigate(`/jobs?${sp.toString()}`, { replace: true });
      }
      return;
    }
    setType(lp);
    setShow(true);
    consumeLandingCta(); // 1회만 노출 (lp/utm 값 자체는 기여 추적용으로 유지)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // 무용수 활동 중에는 레슨 요청 불가(2026-09-26) → 레슨 요청 CTA(student)는 띄우지 않는다.
  //  teacher_sub·academy_job 은 CTA 문구가 없다(작성 화면으로 바로 들어오는 광고) → 아무것도 띄우지 않는다.
  const cta = type && !(type === "student" && !canRequestLessonAs(activeType)) ? CTA[type] : null;
  if (!cta) return null;

  const go = (to) => { setShow(false); if (to) navigate(to); };

  return (
    <ConfirmModal
      isShow={show}
      title={cta.title}
      body={cta.body}
      cancelText={cta.cancelText}
      confirmText={cta.confirmText}
      onCancel={() => { trackClick(`cta:landing_${type}`, { props: { choice: "cancel", to: cta.cancelTo } }); go(cta.cancelTo); }}
      onConfirm={() => { trackClick(`cta:landing_${type}`, { props: { choice: "confirm", to: cta.confirmTo } }); go(cta.confirmTo); }}
    />
  );
}
